/**
 * The list of a QR workspace's WhatsApp numbers (up to 5) plus add / rename / (re)connect / disconnect / remove /
 * per-number settings. Separate from useWhatsAppQrSignup.js, which keeps tracking a workspace's FIRST number only
 * for the create-workspace-by-QR flow. Used by the Integrations page (account owner only; the server enforces it too).
 *
 * State comes from GET .../whatsapp-qr/numbers, polled while the dialog is open (fast while any
 * number is mid-connect so a QR appears promptly, slow otherwise) -- the same polling fallback
 * useWhatsAppQrSignup documents, since a browser socket only holds the current workspace's room.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
const FAST_MS = 3000;
const SLOW_MS = 15000;
const CONNECTING = new Set(["CREATING", "QR_PENDING", "AUTHENTICATING"]);

async function authRequest(method, path, body) {
  const token = await getAuthToken();
  const res = await axios({
    method,
    url: `${SERVER_DOMAIN}${path}`,
    data: body,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return res.data;
}

export function useWhatsAppQrNumbers(workspaceId, enabled) {
  const [connections, setConnections] = useState([]);
  const [maxNumbers, setMaxNumbers] = useState(1);
  const [canAddNumber, setCanAddNumber] = useState(false);
  // Sessions (linked devices) one number may hold, main login included; 1 = multi-session is off for this account (nothing extra is shown).
  const [maxSessions, setMaxSessions] = useState(1);
  const [defaults, setDefaults] = useState({ dailyMessageLimit: 700, burstLimit: 25 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const base = `/api/workspaces/${workspaceId}/whatsapp-qr/numbers`;
  const baseRef = useRef(base);
  baseRef.current = base;

  const refresh = useCallback(async () => {
    if (!workspaceId) return null;
    const data = await authRequest("get", baseRef.current);
    setConnections(data.connections || []);
    setMaxNumbers(data.maxNumbers || 1);
    setCanAddNumber(Boolean(data.canAddNumber));
    setMaxSessions(data.maxSessions || 1);
    if (data.defaults) setDefaults(data.defaults);
    return data;
  }, [workspaceId]);

  useEffect(() => {
    if (!enabled || !workspaceId) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async (first) => {
      let next = SLOW_MS;
      try {
        const data = await refresh();
        if ((data?.connections || []).some((c) => CONNECTING.has(c.state))) next = FAST_MS;
        if (!cancelled) setError(null);
      } catch (err) {
        if (!cancelled && first) setError(err?.response?.data?.message || err.message);
      } finally {
        if (first && !cancelled) setLoading(false);
      }
      if (!cancelled) timer = setTimeout(() => tick(false), next);
    };
    setLoading(true);
    tick(true);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, workspaceId, refresh]);

  // Each action rethrows after recording the message, so a caller can also react (e.g. keep a form open).
  const run = useCallback(
    async (fn) => {
      setError(null);
      try {
        const out = await fn();
        await refresh().catch(() => {});
        return out;
      } catch (err) {
        const message = err?.response?.data?.message || err.message;
        setError(message);
        throw err;
      }
    },
    [refresh]
  );

  // The phone number is asked FIRST: the server checks it (not already linked, not on Meta) before any QR is shown.
  const addNumber = useCallback((label, phoneNumber) => run(() => authRequest("post", baseRef.current, { label, phoneNumber })), [run]);
  const removeNumber = useCallback((id) => run(() => authRequest("delete", `${baseRef.current}/${id}`)), [run]);
  // settings: { dailyMessageLimit, burstLimit } -- whole numbers, or null/"" for the default.
  const saveSettings = useCallback((id, settings) => run(() => authRequest("patch", `${baseRef.current}/${id}/settings`, settings)), [run]);
  const renameNumber = useCallback((id, label) => run(() => authRequest("patch", `${baseRef.current}/${id}`, { label })), [run]);
  const connectNumber = useCallback((id) => run(() => authRequest("post", `${baseRef.current}/${id}/connect`, {})), [run]);
  const logoutNumber = useCallback((id) => run(() => authRequest("post", `${baseRef.current}/${id}/logout`, {})), [run]);
  // Ends the "companion of the Meta number" link (coexistence): the number becomes a normal QR number again.
  const unlinkFromMeta = useCallback((id) => run(() => authRequest("post", `${baseRef.current}/${id}/unlink-meta`, {})), [run]);

  return { connections, maxNumbers, maxSessions, canAddNumber, defaults, loading, error, addNumber, removeNumber, saveSettings, renameNumber, connectNumber, logoutNumber, unlinkFromMeta, refresh };
}

export default useWhatsAppQrNumbers;
